import sum from 'lodash/sum';
import * as PIXI from 'pixi.js';
import { Config } from '../../../video-data/config';
import { TypedSerializableMixin } from '../../serializableMixin';
import { TypedUpdateFlowMixin } from '../../updateFlowMixin';
import {
    AlphaAttribute,
    ColorAttribute,
    NumberAttribute,
    StringAttribute,
    getAttributesToSerialize,
} from '../../attributes';

export type BoxGraphicAnimationXDirection = (typeof Config.ALL_ANIMATION_IN_X_DIRECTION)[number];
export type BoxGraphicAnimationYDirection = (typeof Config.ALL_ANIMATION_IN_Y_DIRECTION)[number];

/**
 * Draws a colored bounding box around the entire text(Metrics)
 *
 */
export class BoxGraphic extends TypedUpdateFlowMixin(TypedSerializableMixin(PIXI.Graphics)) {
    override _classType = 'BoxGraphic';
    override _name = Config.BOX_GRAPHIC;

    @NumberAttribute({ animatable: false })
    accessor positionX = 150;
    @NumberAttribute({ animatable: false })
    accessor positionY = 150;
    @NumberAttribute()
    accessor boxWidth = 50;
    @NumberAttribute()
    accessor boxHeight = 50;
    @NumberAttribute({ min: 0, animatable: false })
    accessor topLeftRadius = 0;
    @NumberAttribute({ min: 0, animatable: false })
    accessor topRightRadius = 0;
    @NumberAttribute({ min: 0, animatable: false })
    accessor bottomRightRadius = 0;
    @NumberAttribute({ min: 0, animatable: false })
    accessor bottomLeftRadius = 0;
    @NumberAttribute({ animatable: false })
    accessor paddingTop = 0;
    @NumberAttribute({ animatable: false })
    accessor paddingBottom = 0;
    @NumberAttribute({ animatable: false })
    accessor paddingLeft = 0;
    @NumberAttribute({ animatable: false })
    accessor paddingRight = 0;
    @ColorAttribute()
    accessor strokeColor = 16777215;
    @AlphaAttribute()
    accessor strokeAlpha = 1;
    @NumberAttribute({ min: 0, animatable: false })
    accessor strokeWidth = 0;
    @ColorAttribute()
    accessor fillColor = 16777215;
    @AlphaAttribute()
    accessor fillAlpha = 1;
    @NumberAttribute({ min: 0 })
    accessor progressYValue: number = 1.0;
    @NumberAttribute({ min: 0 })
    accessor progressXValue = 1.0;
    // The direction in which the box expands when progressXValue moves from 0 to 1.
    // `right` pins the left edge, and expands to the right, `left` pins the right edge and expands to the left.
    @StringAttribute({ choices: Config.ALL_ANIMATION_IN_X_DIRECTION })
    accessor horizontalDirection: BoxGraphicAnimationXDirection = Config.RIGHT_ANIMATION_DIRECTION;
    @StringAttribute({ choices: Config.ALL_ANIMATION_IN_Y_DIRECTION })
    accessor verticalDirection: BoxGraphicAnimationYDirection = Config.CENTER_ANIMATION_DIRECTION;

    #calculateDimensions() {
        const totalHeight = this.boxHeight + this.paddingBottom + this.paddingTop;
        const totalWidth = this.boxWidth + this.paddingLeft + this.paddingRight;
        const percentageHeight = totalHeight * this.progressYValue;
        const percentageWidth = totalWidth * this.progressXValue;
        let positionX = this.positionX;
        let positionY = this.positionY;

        switch (this.horizontalDirection) {
            case 'right':
                // pass
                break;
            case 'left':
                positionX += (1 - this.progressXValue) * totalWidth;
                break;
            case 'center':
                positionX += (totalWidth - percentageWidth) / 2;
                break;
            default:
                throw Error(`Unknown ${this.horizontalDirection}`);
        }

        switch (this.verticalDirection) {
            case 'bottom':
                // pass
                break;
            case 'top':
                positionY += (1 - this.progressYValue) * totalHeight;
                break;
            case 'center':
                positionY += (totalHeight - percentageHeight) / 2;
                break;
            default:
                throw Error(`Unknown ${this.verticalDirection}`);
        }

        return {
            x: positionX,
            y: positionY,
            width: percentageWidth,
            height: percentageHeight,
        };
    }

    #calculateShape(width: number, height: number) {
        let topLeftRadius = this.topLeftRadius;
        let topRightRadius = this.topRightRadius;
        let bottomLeftRadius = this.bottomLeftRadius;
        let bottomRightRadius = this.bottomRightRadius;

        const clampRadiusArray = (radiusArray: Array<number>, maxValue: number): Array<number> => {
            const radiusSum = sum(radiusArray);
            if (maxValue < radiusSum) {
                const fractions = radiusArray.map((radius) => radius / radiusSum);
                return fractions.map((fraction) => maxValue * fraction);
            }
            return radiusArray;
        };

        // left to right intersection
        [topLeftRadius, topRightRadius] = clampRadiusArray([topLeftRadius, topRightRadius], width);
        [bottomLeftRadius, bottomRightRadius] = clampRadiusArray(
            [bottomLeftRadius, bottomRightRadius],
            width,
        );

        // top to bottom intersection
        [topLeftRadius, bottomLeftRadius] = clampRadiusArray(
            [topLeftRadius, bottomLeftRadius],
            height,
        );
        [topRightRadius, bottomRightRadius] = clampRadiusArray(
            [topRightRadius, bottomRightRadius],
            height,
        );

        return { topLeftRadius, topRightRadius, bottomLeftRadius, bottomRightRadius };
    }

    #drawShape(
        x: number,
        y: number,
        width: number,
        height: number,
        topLeftRadius: number,
        topRightRadius: number,
        bottomLeftRadius: number,
        bottomRightRadius: number,
    ) {
        this.clear();
        this.lineStyle(this.strokeWidth, this.strokeColor, this.strokeAlpha);
        this.beginFill(this.fillColor, this.fillAlpha);

        // draw a simple rectangle if no rounded corners are required
        if (
            topLeftRadius === 0 &&
            topRightRadius === 0 &&
            bottomRightRadius === 0 &&
            bottomLeftRadius === 0
        ) {
            this.drawRect(x, y, width, height);
            this.endFill();
            return;
        }

        // draw rectangle with one or more rounded corners
        if (topLeftRadius < 1) {
            this.lineTo(x, y);
        } else {
            this.arc(x + topLeftRadius, y + topLeftRadius, topLeftRadius, Math.PI, -Math.PI / 2);
        }

        if (topRightRadius < 1) {
            this.lineTo(x + width, y);
        } else {
            this.arc(
                x + width - topRightRadius,
                y + topRightRadius,
                topRightRadius,
                -Math.PI / 2,
                0,
            );
        }

        if (bottomRightRadius < 1) {
            this.lineTo(x + width, y + height);
        } else {
            this.arc(
                x + width - bottomRightRadius,
                y + height - bottomRightRadius,
                bottomRightRadius,
                0,
                Math.PI / 2,
            );
        }

        if (bottomLeftRadius < 1) {
            this.lineTo(x, y + height);
        } else {
            this.arc(
                x + bottomLeftRadius,
                y + height - bottomLeftRadius,
                bottomLeftRadius,
                Math.PI / 2,
                Math.PI,
            );
        }
        this.closePath();
        this.endFill();
    }

    override _update() {
        const { x, y, width, height } = this.#calculateDimensions();
        const { topLeftRadius, topRightRadius, bottomLeftRadius, bottomRightRadius } =
            this.#calculateShape(width, height);
        this.#drawShape(
            x,
            y,
            width,
            height,
            topLeftRadius,
            topRightRadius,
            bottomLeftRadius,
            bottomRightRadius,
        );
    }

    override get propertiesToSerialize(): Array<string> {
        return getAttributesToSerialize(this);
    }
}
